import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, Activity } from 'lucide-react';
import { brand } from '../mock/mock';

const PreLoader = ({ onFinish }) => {
  const [progress, setProgress] = React.useState(0);
  const [phase, setPhase] = React.useState('boot');

  React.useEffect(() => {
    let p = 0;
    const iv = setInterval(() => {
      p += Math.random() * 6 + 3;
      if (p >= 100) {
        p = 100;
        clearInterval(iv);
        setPhase('reveal');
        setTimeout(() => {
          setPhase('done');
          setTimeout(() => onFinish && onFinish(), 700);
        }, 900);
      }
      setProgress(Math.min(p, 100));
    }, 90);
    return () => clearInterval(iv);
  }, [onFinish]);

  const logs = [
    'Initializing datacenter core...',
    'Powering Tier-III racks...',
    'Establishing carrier uplinks...',
    'Encrypting network fabric...',
    'Syncing global edge nodes...',
    'DC10G online.'
  ];
  const activeLog = Math.min(logs.length - 1, Math.floor((progress / 100) * logs.length));

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: phase === 'done' ? 0 : 1 }}
      transition={{ duration: 0.6 }}
      className="fixed inset-0 z-[100] bg-[#050914] flex flex-col items-center justify-center overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute inset-0 scanlines pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-radial" />

      {/* Holographic rings around logo */}
      <div className="relative flex items-center justify-center mb-10">
        {[300, 240, 180].map((size, i) => (
          <motion.div
            key={size}
            animate={{ rotate: i % 2 === 0 ? 360 : -360 }}
            transition={{ duration: 12 - i * 2, repeat: Infinity, ease: 'linear' }}
            className="absolute rounded-full border border-cyan-400/30"
            style={{ width: size, height: size, borderStyle: i === 1 ? 'dashed' : 'solid' }}
          />
        ))}

        {/* Corner brackets */}
        <div className="absolute w-40 h-40">
          {['tl', 'tr', 'bl', 'br'].map((c) => (
            <div key={c} className={`absolute w-6 h-6 border-cyan-400 ${
              c === 'tl' ? 'top-0 left-0 border-t-2 border-l-2' :
              c === 'tr' ? 'top-0 right-0 border-t-2 border-r-2' :
              c === 'bl' ? 'bottom-0 left-0 border-b-2 border-l-2' :
              'bottom-0 right-0 border-b-2 border-r-2'
            }`} />
          ))}
        </div>

        {/* Logo center */}
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{
            scale: phase === 'reveal' ? 1.15 : 1,
            opacity: 1
          }}
          transition={{ duration: 0.8 }}
          className="relative w-32 h-32 rounded-2xl bg-white/95 backdrop-blur flex items-center justify-center p-4"
          style={{ boxShadow: '0 0 60px rgba(34, 211, 238, 0.6), 0 0 120px rgba(14, 165, 233, 0.3)' }}
        >
          <img src={brand.logo} alt="DC10G" className="w-full h-full object-contain" />
        </motion.div>

        {/* Orbit satellites */}
        {[{ Icon: Zap, deg: 0 }, { Icon: Shield, deg: 120 }, { Icon: Activity, deg: 240 }].map(({ Icon, deg }, i) => (
          <motion.div
            key={i}
            className="absolute"
            animate={{ rotate: [deg, deg + 360] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: '0 0' }}
          >
            <div style={{ transform: 'translateY(-150px)' }}>
              <motion.div
                animate={{ rotate: [-deg, -deg - 360] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
                className="w-11 h-11 rounded-xl bg-cyan-500/20 backdrop-blur border border-cyan-400/50 flex items-center justify-center"
              >
                <Icon size={18} className="text-cyan-300" />
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="text-center">
        <div className="text-[10px] tracking-[0.5em] text-cyan-400 uppercase">Enterprise Cloud · Datacenter</div>
      </motion.div>

      {/* Progress terminal */}
      <div className="mt-10 w-[320px] md:w-[480px]">
        <div className="h-1 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-cyan-400 via-sky-400 to-cyan-300"
            style={{ width: `${progress}%`, boxShadow: '0 0 12px rgba(34,211,238,0.9)' }}
          />
        </div>
        <div className="flex items-center justify-between mt-3 text-[11px] font-mono">
          <span className="text-cyan-300">
            <span className="cursor-blink">▸</span> {logs[activeLog]}
          </span>
          <span className="text-cyan-500">{String(Math.floor(progress)).padStart(3, '0')}%</span>
        </div>
      </div>

      <div className="absolute bottom-10 flex items-end gap-1 h-14">
        {Array.from({ length: 32 }).map((_, i) => (
          <div
            key={i}
            className="w-1 bg-gradient-to-t from-cyan-500 via-sky-400 to-cyan-200 rounded-full server-bar"
            style={{ animationDelay: `${i * 0.06}s` }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default PreLoader;
